"use client";

import { useState, useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { Globe } from "lucide-react";
import { useCountry } from "@/packages/context/CountryContext";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";

interface CountryCurrencySelectorProps {
  className?: string;
}

export default function CountryCurrencySelector({ className = "" }: CountryCurrencySelectorProps) {
  const [mounted, setMounted] = useState(false);
  const { selectedCountryId, countryOptions, changeCountry } = useCountry();
  const searchParams = useSearchParams();
  const { fastPush } = useFastRouter();

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleSelectionChange = (val: string) => {
    const selected = countryOptions.find((c) => String(c.countryId) === val);
    if (selected) {
      changeCountry(selected);

      // Sync top URL parameter to match the new country's currency code
      const params = new URLSearchParams(searchParams.toString());
      params.set("currency", selected.currencyCode);
      fastPush(`?${params.toString()}`);
    }
  };


  const activeCountry = countryOptions.find((c) => c.countryId === selectedCountryId) || countryOptions[0];

  return (
    <div className={`relative flex items-center cursor-pointer ${className}`}>
      {!mounted || countryOptions.length === 0 ? (
        <div className="flex items-center gap-1.5 text-slate-600 text-xs font-medium">
          <Globe className="w-3.5 h-3.5 text-slate-500" />
          <span>Ship to</span>
        </div>
      ) : (
        <Select value={selectedCountryId ? String(selectedCountryId) : undefined} onValueChange={handleSelectionChange}>
          <SelectTrigger
            aria-label="Select Country & Currency"
            className="flex items-center gap-1.5 !border-none bg-transparent !shadow-none hover:text-[var(--brand-accent)] transition-colors duration-200 !outline-none !ring-0 focus:!ring-0 focus:!outline-none focus-visible:!ring-0 focus-visible:!outline-none focus-visible:!border-none data-[state=open]:!border-none data-[state=open]:!ring-0 data-[state=open]:!outline-none p-0 cursor-pointer text-xs font-semibold h-auto"
          >
            <Globe className="w-3.5 h-3.5 text-slate-500 shrink-0" />
            <SelectValue placeholder={activeCountry ? `${activeCountry.countryName} (${activeCountry.currencyCode})` : "Ship to"} />
          </SelectTrigger>

          <SelectContent className="z-[999] bg-white border border-slate-200 rounded-md shadow-md max-h-60 overflow-y-auto">
            {countryOptions.map((c) => (
              <SelectItem key={c.countryId} value={String(c.countryId)} className="text-xs font-semibold cursor-pointer">
                {c.countryName} — {c.currencySymbol} {c.currencyCode}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
